import { WorkerInfo } from '../types/auth';
import { Task, WorkOrder } from '../types/workOrders';

const TELEGRAM_BOT_TOKEN = import.meta.env.VITE_TELEGRAM_BOT_TOKEN;
const TELEGRAM_API_BASE = 'https://api.telegram.org';

interface TelegramResponse {
    ok: boolean;
    result?: any;
    description?: string;
}

export async function sendTelegramFile(
    chatId: string,
    file: File,
    caption?: string,
): Promise<void> {
    if (!chatId || !file) {
        console.warn('Missing required parameters for Telegram file upload');
        return;
    }

    try {
        const formData = new FormData();
        formData.append('chat_id', chatId);
        formData.append('document', file);
        if (caption) {
            formData.append('caption', caption);
            formData.append('parse_mode', 'HTML');
        }

        const response = await fetch(
            `${TELEGRAM_API_BASE}/bot${TELEGRAM_BOT_TOKEN}/sendDocument`,
            {
                method: 'POST',
                body: formData,
            },
        );

        const data: TelegramResponse = await response.json();

        if (!response.ok || !data.ok) {
            console.error('Telegram File Upload Error:', {
                status: response.status,
                ok: data.ok,
                description: data.description,
                fileName: file.name,
                fileSize: file.size,
            });
            throw new Error(
                data.description || 'Failed to send file via Telegram',
            );
        }
    } catch (error) {
        console.error('Telegram file upload error:', {
            error,
            fileName: file.name,
            fileSize: file.size,
        });
        // Don't throw error to allow the process to continue even if file upload fails
    }
}

export async function sendTelegramMessage(
    chatId: string,
    message: string,
): Promise<void> {
    if (!chatId || !message) {
        console.warn('Missing required parameters for Telegram notification');
        return;
    }

    try {
        const response = await fetch(
            `${TELEGRAM_API_BASE}/bot${TELEGRAM_BOT_TOKEN}/sendMessage`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    chat_id: chatId,
                    text: message,
                    parse_mode: 'HTML',
                }),
            },
        );

        const data: TelegramResponse = await response.json();

        if (!response.ok || !data.ok) {
            // Log more detailed error information
            console.error('Telegram API Error:', {
                status: response.status,
                ok: data.ok,
                description: data.description,
                chatId,
                messageLength: message.length,
            });
            throw new Error(
                data.description || 'Failed to send Telegram notification',
            );
        }
    } catch (error) {
        console.error('Comprehensive Telegram notification error:', {
            error,
            chatId,
            messageLength: message?.length,
            botToken: TELEGRAM_BOT_TOKEN ? 'Present' : 'Missing',
        });
        throw error; // Rethrow to allow caller to handle
    }
}

export function formatTaskStartMessage(
    worker: WorkerInfo,
    task: Task,
    workOrder: WorkOrder,
): string {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });

    return (
        `🟢 <b>Task Started</b>\n\n` +
        `<b>Worker:</b> ${worker.name}\n` +
        `<b>WO#:</b> ${workOrder.wo_number}\n` +
        `<b>Work Type:</b> ${workOrder.work_type_alias}\n` +
        `<b>Address:</b> ${workOrder.address}, ${workOrder.city}, ${workOrder.state} ${workOrder.zip}\n\n` +
        `<b>Task:</b> ${task.desc}\n` +
        `<b>Start Time:</b> ${timestamp} CST\n\n` +
        `<i>This is an automated notification.</i>`
    );
}

export function formatTaskEndMessage(
    worker: WorkerInfo,
    task: Task,
    workOrder: WorkOrder,
    duration: string,
): string {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });

    return (
        `🔴 <b>Task Completed</b>\n\n` +
        `<b>Worker:</b> ${worker.name}\n` +
        `<b>WO#:</b> ${workOrder.wo_number}\n` +
        `<b>Work Type:</b> ${workOrder.work_type_alias}\n` +
        `<b>Address:</b> ${workOrder.address}, ${workOrder.city}, ${workOrder.state} ${workOrder.zip}\n\n` +
        `<b>Task:</b> ${task.desc}\n` +
        `<b>Duration:</b> ${duration}\n` +
        `<b>End Time:</b> ${timestamp} CST\n\n` +
        `<i>This is an automated notification.</i>`
    );
}

export function formatExpenseMessage(
    worker: WorkerInfo,
    amount: number,
    comment: string,
): string {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });

    const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);

    const fileInfo = worker.files?.length
        ? `<b>Attachments:</b> ${worker.files.length} file${worker.files.length > 1 ? 's' : ''}\n`
        : '';
    return (
        `💰 <b>New Expense Added</b>\n\n` +
        `<b>Worker:</b> ${worker.name}\n` +
        `<b>Amount:</b> ${formattedAmount}\n` +
        `<b>Category:</b> ${worker.categoryName || 'N/A'}\n` +
        `<b>Work Order:</b> ${worker.woNumber ? `#${worker.woNumber}` : 'N/A'}\n` +
        `<b>Description:</b> ${comment || 'No description'}\n` +
        `<b>Time:</b> ${timestamp} CST\n` +
        fileInfo +
        `<i>This is an automated notification.</i>`
    );
}

export function formatClockInMessage(worker: WorkerInfo): string {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });

    return (
        `🟢 <b>Worker Clocked In</b>\n\n` +
        `<b>Worker:</b> ${worker.name}\n` +
        `<b>Time:</b> ${timestamp} CST\n\n` +
        `<i>This is an automated notification.</i>`
    );
}

export function formatJobFinishMessage(
    worker: WorkerInfo,
    duration: string,
): string {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });

    return (
        `🟡 <b>Worker Finished Job</b>\n\n` +
        `<b>Worker:</b> ${worker.name}\n` +
        `<b>Duration:</b> ${duration}\n` +
        `<b>Time:</b> ${timestamp} CST\n\n` +
        `<i>This is an automated notification.</i>`
    );
}

export function formatClockOutMessage(
    worker: WorkerInfo,
    duration: string,
): string {
    const timestamp = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    });

    return (
        `🔴 <b>Worker Clocked Out</b>\n\n` +
        `<b>Worker:</b> ${worker.name}\n` +
        `<b>Total Duration:</b> ${duration}\n` +
        `<b>Time:</b> ${timestamp} CST\n\n` +
        `<i>This is an automated notification.</i>`
    );
}
